import {
  Component,
  ChangeDetectionStrategy,
  OnInit,
  ChangeDetectorRef,
  inject,
  ViewChild,
  TemplateRef,
  Input,
  OnDestroy,
} from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TabsModule } from 'primeng/tabs';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { PaginatorModule, PaginatorState } from 'primeng/paginator';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { FormsModule } from '@angular/forms';
import { CheckboxModule } from 'primeng/checkbox';
import { Toast } from 'primeng/toast';
import { BackButton } from '@components/ui/back-button/back-button';
import { Loading } from '@components/ui/loading/loading';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { MessageService } from 'primeng/api';
import { AddTask } from './add-task/add-task';
import { TaskSchedulerService } from '@services/task-scheduler.service';
import { TaskResultDisplayItem } from '@models/task-scheduler.models';
import { IconField } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';
import { DataTableComponent, TableColumn } from '@components/data-table/data-table.component';
import {
  SystemBackgroundTask,
  SystemBackgroundTaskService,
  SystemBackgroundTaskResult,
  SystemBackgroundTaskResultService,
  SystemBackgroundTaskResultBaseGetQuery,
  LocalizationString,
  SortType,
} from '@6grain/masofa-monolith-api-client/common';
import { FieldFilter } from '@6grain/masofa-monolith-api-client/dictionaries';
import { ActivatedRoute } from '@angular/router';
import { CustomMessageService } from '@app/interceptors/custom-message';
import { LangChangeEvent } from '@ngx-translate/core';
import { Observable, Subscription, forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';
import { CustomPaginatorComponent } from '../../Ui-Kit/custom-paginator/custom-paginator.component';

@Component({
  selector: 'app-task-scheduler-page',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ButtonModule,
    InputTextModule,
    TabsModule,
    TableModule,
    TagModule,
    PaginatorModule,
    ToggleSwitchModule,
    FormsModule,
    CheckboxModule,
    Toast,
    BackButton,
    Loading,
    IconField,
    InputIcon,
    DataTableComponent,
    CustomPaginatorComponent,
  ],
  providers: [
    DialogService,
    DatePipe,
    {
      provide: MessageService,
      useClass: CustomMessageService,
    },
  ],
  templateUrl: './task-scheduler-page.html',
  styleUrl: './task-scheduler-page.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TaskSchedulerPageComponent implements OnInit, OnDestroy {
  private systemBackgroundTaskService = inject(SystemBackgroundTaskService);
  private systemBackgroundTaskResultService = inject(SystemBackgroundTaskResultService);
  private translateService = inject(TranslateService);
  private datePipe = inject(DatePipe);
  private langChangeSubscription?: Subscription;

  @ViewChild('statusTemplate', { static: true }) statusTemplate!: TemplateRef<any>;
  @ViewChild('errorsTemplate', { static: true }) errorsTemplate!: TemplateRef<any>;

  @Input() filterParams: FieldFilter | null = null;
  currentSystemTask: SystemBackgroundTask | null = null;

  lang = this.translateService.currentLang as keyof LocalizationString;

  loading: boolean = true;
  loadingSystemBackgroundTask: boolean = true;
  loadingHistory: boolean = false;
  activeTab: string = '0';
  searchQuery: string = '';
  isSmallScreen: boolean = false;

  pageSize: number = 10;
  totalRecords: number = 0;

  selectedTask: SystemBackgroundTask | null = null;
  selectAll: boolean = false;
  selectedExecutionId: string | null = null;

  taskSchedulerService = inject(TaskSchedulerService);
  private cdr = inject(ChangeDetectorRef);
  private ref: DynamicDialogRef | undefined;
  private historyRequestId = 0;
  private taskResultsRequestId = 0;

  private static readonly MAX_RESULTS_TAKE = 200;
  private static readonly TASK_DETAIL_TAKE = 50;

  rowsPerPageOptions = [
    { label: '10', value: 10 },
    { label: '25', value: 25 },
    { label: '50', value: 50 },
    { label: '100', value: 100 },
  ];

  allTasks: SystemBackgroundTask[] = [];
  scheduledTasks: SystemBackgroundTask[] = [];
  filteredTasks: SystemBackgroundTask[] = [];

  executionHistory: TaskResultDisplayItem[] = [];
  paginatedExecutionHistory: TaskResultDisplayItem[] = [];
  historyFirst: number = 0;
  historyRows: number = 10;
  historyTotalRecords: number = 0;
  taskResultsTotal: number = 0;

  executionHistoryFiltered: any[] = [];
  tableColumns: TableColumn[] = [];

  protected filterKeywords: string[] = [];
  parentRoute: string = '/admin';

  constructor(
    private dialogService: DialogService,
    private messageService: MessageService,
    private translate: TranslateService,
    private route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    const routeData = this.route.snapshot.data;
    if (routeData['parentRoute']) {
      this.parentRoute = routeData['parentRoute'];
    }
    if (routeData['filterKeywords'] && Array.isArray(routeData['filterKeywords'])) {
      this.filterKeywords = routeData['filterKeywords'];
    }

    const queryParams = this.route.snapshot.queryParams;
    if (queryParams['module']) {
      this.filterParams = {
        filterField: 'module',
        filterValue: queryParams['module'],
        filterOperator: 0,
      };
    }

    this.langChangeSubscription = this.translate.onLangChange.subscribe((event: LangChangeEvent) => {
      this.lang = event.lang as keyof LocalizationString;
      this.cdr.markForCheck();
    });

    this.tableColumns = [
      {
        field: 'createdAt',
        header: 'Created At',
        type: 'date',
        defaultSorted: true,
      },
      { field: 'resultType', header: 'Status', type: 'custom', customTemplate: this.statusTemplate },
      { field: 'errorMessages', header: 'Errors', type: 'custom', customTemplate: this.errorsTemplate },
    ];

    this.loadData();
  }

  ngOnDestroy(): void {
    if (this.langChangeSubscription) {
      this.langChangeSubscription.unsubscribe();
    }
  }

  private loadData(): void {
    this.loading = true;
    this.cdr.markForCheck();

    this.systemBackgroundTaskService.commonSystemBackgroundTaskGetByQueryPost({}).subscribe({
      next: (tasks) => {
        this.allTasks = tasks;

        if (this.filterKeywords.length > 0) {
          this.scheduledTasks = tasks.filter((task) => {
            const typeName = task.executeTypeName || '';
            const isMatch = this.filterKeywords.some((keyword) => typeName.includes(keyword));
            return isMatch;
          });
        } else {
          this.scheduledTasks = tasks;
        }
        this.filteredTasks = this.scheduledTasks;
        this.totalRecords = this.scheduledTasks.length;
        this.loading = false;
        this.historyFirst = 0;
        this.cdr.markForCheck();
        this.loadHistory();
      },
      error: (err) => {
        console.error('Error loading tasks:', err);
        this.loading = false;
        this.cdr.markForCheck();
      },
    });
  }

  private loadHistory(): void {
    this.loadingHistory = true;
    this.cdr.markForCheck();

    const requestId = ++this.historyRequestId;
    const taskIds = this.getFilteredHistoryTaskIds();

    if (this.filterKeywords.length > 0 && taskIds.length === 0) {
      this.setHistoryPage([], 0, requestId);
      return;
    }

    const history$ =
      taskIds.length > 1
        ? this.loadHistoryForTaskIds(taskIds, this.historyRows, this.historyFirst)
        : this.loadHistoryPage(taskIds[0] ?? null, this.historyRows, this.historyFirst);

    history$.subscribe({
      next: ({ items, total }) => {
        this.setHistoryPage(items, total, requestId);
      },
      error: (err) => {
        if (requestId !== this.historyRequestId) {
          return;
        }
        console.error('Error loading task results:', err);
        this.loadingHistory = false;
        this.cdr.markForCheck();
      },
    });
  }

  private getFilteredHistoryTaskIds(): string[] {
    if (this.filterKeywords.length === 0) {
      return [];
    }

    return this.scheduledTasks.map((task) => task.id).filter((id): id is string => !!id);
  }

  private loadHistoryPage(
    taskId: string | null,
    take: number,
    offset: number,
  ): Observable<{ items: TaskResultDisplayItem[]; total: number }> {
    const query = this.buildResultQuery({ take, offset, taskId });
    const countQuery = this.buildResultQuery({ take, offset: 0, taskId });

    return forkJoin({
      results: this.systemBackgroundTaskResultService.commonSystemBackgroundTaskResultGetByQueryPost(query),
      total: this.systemBackgroundTaskResultService.commonSystemBackgroundTaskResultGetTotalCountPost(countQuery),
    }).pipe(
      map(({ results, total }) => ({
        items: this.mapResultsToHistory(results),
        total,
      })),
    );
  }

  private loadHistoryForTaskIds(
    taskIds: string[],
    take: number,
    offset: number,
  ): Observable<{ items: TaskResultDisplayItem[]; total: number }> {
    const perTaskTake = Math.min(offset + take, TaskSchedulerPageComponent.MAX_RESULTS_TAKE);

    return forkJoin({
      pages: forkJoin(
        taskIds.map((id) =>
          this.systemBackgroundTaskResultService.commonSystemBackgroundTaskResultGetByQueryPost(
            this.buildResultQuery({ take: perTaskTake, offset: 0, taskId: id }),
          ),
        ),
      ),
      totals: forkJoin(
        taskIds.map((id) =>
          this.systemBackgroundTaskResultService.commonSystemBackgroundTaskResultGetTotalCountPost(
            this.buildResultQuery({ take: perTaskTake, offset: 0, taskId: id }),
          ),
        ),
      ),
    }).pipe(
      map(({ pages, totals }) => {
        const items = this.mapResultsToHistory(pages.flat()).slice(offset, offset + take);
        const total = totals.reduce((sum, count) => sum + count, 0);
        return { items, total };
      }),
    );
  }

  private buildResultQuery(options: {
    take: number;
    offset: number;
    taskId?: string | null;
  }): SystemBackgroundTaskResultBaseGetQuery {
    const query: SystemBackgroundTaskResultBaseGetQuery = {
      take: options.take,
      offset: options.offset,
      sortBy: 'createAt',
      sort: SortType.NUMBER_1,
      filters: [],
    };

    if (options.taskId) {
      query.filters = [
        {
          filterField: 'systemBackgroundTaskId',
          filterValue: options.taskId,
          filterOperator: 0,
        },
      ];
    }

    return query;
  }

  private mapResultsToHistory(results: SystemBackgroundTaskResult[]): TaskResultDisplayItem[] {
    return results
      .map((result) => {
        let task = this.allTasks.find((item) => item.id === result.systemBackgroundTaskId);

        if (!task && result.taskResultJsonTypeName) {
          const shortResultName = result.taskResultJsonTypeName.split('.').pop()?.replace('Result', '') || '';

          task = this.allTasks.find((item) => {
            if (!item.executeTypeName) {
              return false;
            }
            const shortTaskName = item.executeTypeName.split('.').pop() || '';
            return shortTaskName === shortResultName;
          });
        }

        let isMatch = true;
        if (this.filterKeywords.length > 0) {
          if (task?.executeTypeName) {
            isMatch = this.filterKeywords.some((keyword) => task!.executeTypeName!.includes(keyword));
          } else {
            const typeName = result.taskResultJsonTypeName?.toLowerCase() || '';
            isMatch = this.filterKeywords.some((keyword) => typeName.includes(keyword.toLowerCase()));
          }
        }

        if (!isMatch) {
          return null;
        }

        const fallbackName = result.taskResultJsonTypeName?.split('.').pop()?.replace('Result', '') || 'Unknown Task';

        return {
          id: result.id || '',
          taskName: task?.names || {
            'en-US': fallbackName,
            'ru-RU': fallbackName,
            'uz-Latn-UZ': fallbackName,
          },
          executeTypeName: task?.executeTypeName || result.taskResultJsonTypeName,
          resultType: result.resultType,
          createdAt: result.createAt,
          status: result.resultType === 0 ? 'success' : 'failed',
        } as TaskResultDisplayItem;
      })
      .filter((item): item is TaskResultDisplayItem => item !== null)
      .sort((a, b) => {
        const dateA = new Date(a.createdAt ?? 0).getTime();
        const dateB = new Date(b.createdAt ?? 0).getTime();
        return dateB - dateA;
      });
  }

  private setHistoryPage(items: TaskResultDisplayItem[], total: number, requestId: number): void {
    if (requestId !== this.historyRequestId) {
      return;
    }

    this.executionHistory = items;
    this.paginatedExecutionHistory = items;
    this.historyTotalRecords = total;
    this.loadingHistory = false;
    this.cdr.markForCheck();
  }

  onHistoryPageChange(event: PaginatorState): void {
    this.historyFirst = event.first ?? 0;
    this.historyRows = event.rows ?? this.historyRows;
    this.loadHistory();
  }

  onHistoryPageSizeChange(newSize: number): void {
    this.historyRows = newSize;
    this.historyFirst = 0;
    this.loadHistory();
  }

  addNewTask(): void {
    this.ref = this.dialogService.open(AddTask, {
      draggable: false,
      resizable: false,
      header: this.translate.instant('Add a new task'),
      width: '1000px',
      height: 'auto',
      contentStyle: { overflow: 'visible' },
      baseZIndex: 10000,
      maximizable: false,
      modal: true,
      closable: true,
      styleClass: 'add-task-dialog',
      data: {
        filterKeywords: this.filterKeywords,
      },
    });

    this.ref.onClose.subscribe((result: any) => {
      if (result?.success) {
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('Success'),
          detail: this.translate.instant('Task created successfully'),
        });
        this.loadData();
      }
    });
  }

  editTask(task: SystemBackgroundTask): void {
    this.ref = this.dialogService.open(AddTask, {
      draggable: false,
      resizable: false,
      header: this.translate.instant('Edit task'),
      width: '1000px',
      height: 'auto',
      contentStyle: { overflow: 'visible' },
      baseZIndex: 10000,
      maximizable: false,
      modal: true,
      closable: true,
      styleClass: 'add-task-dialog',
      data: {
        task,
        possibleExecuteType: this.selectedTask,
        filterKeywords: this.filterKeywords,
      },
    });

    this.ref.onClose.subscribe((result: any) => {
      if (result?.success) {
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('Success'),
          detail: this.translate.instant('Task updated successfully'),
        });
      }
    });

    this.loadData();
  }

  deleteTask(task: SystemBackgroundTask): void {
    this.systemBackgroundTaskService.commonSystemBackgroundTaskDeleteIdDelete(task.id!).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('Success'),
          detail: this.translate.instant('Task deleted successfully'),
        });
      },
      error: (err) => {
        console.error('Error deleting task:', err);
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('Error'),
          detail: this.translate.instant('Failed to delete task'),
        });
      },
    });

    this.loadData();
  }

  selectExecution(execution: TaskResultDisplayItem): void {
    this.selectedExecutionId = this.selectedExecutionId === execution.id ? null : execution.id;
  }

  selectTask(task: SystemBackgroundTask): void {
    this.selectedTask = task;

    this.currentSystemTask = null;
    this.executionHistoryFiltered = [];
    this.taskResultsTotal = 0;

    if (task.executeTypeName) {
      this.loadingSystemBackgroundTask = true;
      this.cdr.markForCheck();

      const requestId = ++this.taskResultsRequestId;
      const resultsQuery = this.buildResultQuery({
        take: TaskSchedulerPageComponent.TASK_DETAIL_TAKE,
        offset: 0,
        taskId: task.id,
      });

      forkJoin({
        results: this.systemBackgroundTaskResultService.commonSystemBackgroundTaskResultGetByQueryPost(resultsQuery),
        total: this.systemBackgroundTaskResultService.commonSystemBackgroundTaskResultGetTotalCountPost({
          filters: resultsQuery.filters,
        }),
      }).subscribe({
        next: ({ results, total }) => {
          if (requestId !== this.taskResultsRequestId) {
            return;
          }

          this.taskResultsTotal = total;
          this.executionHistoryFiltered = results.map((result) => {
            let errorMessages = '';
            let hasErrors = false;
            const resultType = result.resultType ?? 0;

            try {
              if (result.taskResultJson) {
                const resultJson =
                  typeof result.taskResultJson === 'string' ? JSON.parse(result.taskResultJson) : result.taskResultJson;
                if (
                  resultJson &&
                  resultJson.Errors &&
                  Array.isArray(resultJson.Errors) &&
                  resultJson.Errors.length > 0
                ) {
                  hasErrors = true;
                  errorMessages = resultJson.Errors.join(', ');
                }
              }
            } catch (e) {
              console.error('Error parsing taskResultJson', e);
            }

            if (resultType !== 0) {
              hasErrors = true;
              if (!errorMessages) {
                errorMessages = 'Failed (No error details)';
              }
            }

            return {
              id: result.id || '',
              createdAt: result.createAt,
              createdAtFormatted: this.datePipe.transform(result.createAt, 'yyyy-MM-dd'),
              hasErrors: hasErrors,
              errorMessages: errorMessages,
              resultType: resultType,
              status: resultType === 0 ? 'success' : 'failed',
            };
          });
          this.cdr.markForCheck();
        },
        error: (err) => {
          if (requestId !== this.taskResultsRequestId) {
            return;
          }
          console.error('Error loading task results:', err);
        },
      });

      this.systemBackgroundTaskService
        .commonSystemBackgroundTaskGetByQueryPost({
          filters: [
            {
              filterField: 'executeTypeName',
              filterValue: task.executeTypeName,
              filterOperator: 0,
            },
          ],
        })
        .subscribe({
          next: (tasks) => {
            if (tasks && tasks.length > 0) {
              this.currentSystemTask = tasks[0];
            }
            this.loadingSystemBackgroundTask = false;
            this.cdr.markForCheck();
          },
          error: (err) => {
            console.error('Error loading SystemBackgroundTask:', err);
            this.messageService.add({
              severity: 'error',
              summary: this.translate.instant('Error'),
              detail: this.translate.instant('Failed to load task details'),
            });
            this.loadingSystemBackgroundTask = false;
            this.cdr.markForCheck();
          },
        });
    }
  }

  getActiveTasksCount(): number {
    return this.scheduledTasks.filter((task) => task.isActive).length;
  }

  updateTaskStatus(task: SystemBackgroundTask, isActive: boolean): void {
    let systemBackgroundTask: SystemBackgroundTask = task;
    systemBackgroundTask.isActive = isActive;

    this.systemBackgroundTaskService.commonSystemBackgroundTaskUpdatePut(systemBackgroundTask).subscribe({
      next: (response) => {
        const status = isActive ? 'activated' : 'deactivated';
        this.messageService.add({
          severity: 'success',
          summary: 'Status Updated',
          detail: task.names?.['en-US'] ?? '',
        });
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: this.translate.instant('Failed to update task status'),
        });
        task.isActive = !isActive;
        this.cdr.markForCheck();
      },
    });
  }

  onSearch(): void {
    if (!this.searchQuery || this.searchQuery.trim() === '') {
      this.filteredTasks = this.scheduledTasks;
      return;
    }
    const query = this.searchQuery.toLowerCase();
    this.filteredTasks = this.scheduledTasks.filter((task) => {
      const name = task.names?.[this.lang]?.toLowerCase() || '';
      return name.includes(query);
    });
  }
}
